/**
 * Collection Page JavaScript
 * Handles mobile filter drawer, infinite scroll, and AJAX filtering
 */

class CollectionPage {
  constructor() {
    this.initMobileFilters();
    this.initInfiniteScroll();
    this.initLazyLoading();
    this.initAccessibility();
    this.initCollectionDescription();
  }

  /**
   * Initialize mobile filter drawer functionality
   */
  initMobileFilters() {
    const filterButton = document.querySelector('.mobile-facets__open-wrapper');
    const filterDrawer = document.querySelector('.mobile-facets__wrapper');
    const closeButton = document.querySelector('.mobile-facets__close-button');
    const backdrop = document.querySelector('.mobile-facets');

    if (!filterButton || !filterDrawer) return;

    // Open filter drawer
    filterButton.addEventListener('click', () => {
      filterDrawer.setAttribute('open', '');
      filterButton.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
      
      // Focus management
      const closeButton = filterDrawer.querySelector('.mobile-facets__close-button');
      if (closeButton) {
        setTimeout(() => closeButton.focus(), 100);
      }
      
      // Announce to screen readers
      this.announceToScreenReader('Filter menu opened');
    });

    // Close filter drawer
    const closeDrawer = () => {
      filterDrawer.removeAttribute('open');
      filterButton.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
      filterButton.focus();
      
      // Announce to screen readers
      this.announceToScreenReader('Filter menu closed');
    };

    closeButton?.addEventListener('click', closeDrawer);

    // Close on backdrop click
    backdrop?.addEventListener('click', (e) => {
      if (e.target === backdrop) {
        closeDrawer();
      }
    });

    // Close on Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && filterDrawer.hasAttribute('open')) {
        closeDrawer();
      }
    });

    // Swipe to close (touch devices)
    let startX = 0;
    let currentX = 0;
    let isSwiping = false;

    backdrop?.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
      isSwiping = true;
    });

    backdrop?.addEventListener('touchmove', (e) => {
      if (!isSwiping) return;
      currentX = e.touches[0].clientX;
      const diffX = currentX - startX;
      
      if (diffX > 0) {
        e.preventDefault();
        backdrop.style.transform = `translateX(${Math.min(diffX, 300)}px)`;
      }
    });

    backdrop?.addEventListener('touchend', () => {
      if (!isSwiping) return;
      const diffX = currentX - startX;
      
      if (diffX > 100) {
        closeDrawer();
      } else {
        backdrop.style.transform = '';
      }
      
      isSwiping = false;
      startX = 0;
      currentX = 0;
    });

    // Handle form submissions
    this.initFilterForms();
  }

  /**
   * Initialize filter form handling
   */
  initFilterForms() {
    const forms = document.querySelectorAll('#FacetFiltersForm, #FacetFiltersFormDesktop');
    
    forms.forEach(form => {
      // Handle sort dropdown change
      const sortSelect = form.querySelector('#SortBy');
      if (sortSelect) {
        sortSelect.addEventListener('change', () => {
          this.updateCollection(new FormData(form));
        });
      }

      // Handle filter checkboxes
      const checkboxes = form.querySelectorAll('input[type="checkbox"], input[type="radio"]');
      checkboxes.forEach(checkbox => {
        checkbox.addEventListener('change', () => {
          // Add small delay to allow for better UX
          setTimeout(() => {
            this.updateCollection(new FormData(form));
          }, 100);
        });
      });
    });
  }

  /**
   * Update collection via AJAX
   */
  async updateCollection(formData) {
    const loadingOverlay = document.querySelector('.loading-overlay');
    const productGrid = document.querySelector('#product-grid');
    const productCount = document.querySelector('#ProductCount');
    
    try {
      // Show loading state
      loadingOverlay?.classList.add('active');
      
      // Build URL with filters
      const url = new URL(window.location);
      const params = new URLSearchParams(formData);
      
      // Clear existing params and add new ones
      url.search = '';
      for (const [key, value] of params) {
        if (value && value !== '') {
          url.searchParams.append(key, value);
        }
      }

      // Add section_id for AJAX
      url.searchParams.append('section_id', 'collection-products');
      
      const response = await fetch(url);
      if (!response.ok) throw new Error('Network response was not ok');
      
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Update product grid
      const newProductGrid = doc.querySelector('#product-grid');
      if (newProductGrid && productGrid) {
        productGrid.innerHTML = newProductGrid.innerHTML;
        
        // Reinitialize lazy loading for new images
        this.initLazyLoading();
        
        // Trigger animation for new products
        this.animateNewProducts();
      }
      
      // Update product count
      const newProductCount = doc.querySelector('#ProductCount');
      if (newProductCount && productCount) {
        const oldCount = productCount.textContent.trim();
        const newCount = newProductCount.textContent.trim();
        productCount.innerHTML = newProductCount.innerHTML;
        
        // Announce count change to screen readers
        if (oldCount !== newCount) {
          this.announceToScreenReader(`Products updated. ${newCount}`);
        }
      }
      
      // Update URL without page reload
      window.history.replaceState({}, '', url.pathname + url.search);
      
      // Close mobile drawer after applying filters
      const filterDrawer = document.querySelector('.mobile-facets__wrapper');
      const filterButton = document.querySelector('.mobile-facets__open-wrapper');
      if (filterDrawer?.hasAttribute('open')) {
        filterDrawer.removeAttribute('open');
        if (filterButton) {
          filterButton.setAttribute('aria-expanded', 'false');
        }
        document.body.style.overflow = '';
      }
      
    } catch (error) {
      console.error('Error updating collection:', error);
    } finally {
      loadingOverlay?.classList.remove('active');
    }
  }

  /**
   * Initialize infinite scroll functionality
   */
  initInfiniteScroll() {
    const trigger = document.querySelector('#InfiniteScrollTrigger');
    const loading = document.querySelector('#InfiniteScrollLoading');
    
    if (!trigger) return;
    
    let isLoading = false;
    let currentPage = parseInt(trigger.dataset.page);
    const totalPages = parseInt(trigger.dataset.pages);
    const collectionUrl = trigger.dataset.collectionUrl;
    
    const observer = new IntersectionObserver(
      async (entries) => {
        const [entry] = entries;
        
        if (entry.isIntersecting && !isLoading && currentPage < totalPages) {
          isLoading = true;
          loading.classList.remove('infinite-scroll-loading--hidden');
          
          try {
            const nextPage = currentPage + 1;
            const url = new URL(`${collectionUrl}?page=${nextPage}`, window.location.origin);
            url.searchParams.append('section_id', 'collection-products');
            
            // Preserve current filters and sort
            const currentParams = new URLSearchParams(window.location.search);
            for (const [key, value] of currentParams) {
              if (key !== 'page' && key !== 'section_id') {
                url.searchParams.set(key, value);
              }
            }
            
            const response = await fetch(url);
            if (!response.ok) throw new Error('Network response was not ok');
            
            const html = await response.text();
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');
            
            const newProducts = doc.querySelectorAll('#product-grid .collection-grid__item');
            const productGrid = document.querySelector('#product-grid');
            
            if (newProducts.length && productGrid) {
              newProducts.forEach(product => {
                productGrid.appendChild(product);
              });
              
              currentPage = nextPage;
              trigger.dataset.page = currentPage;
              
              // Reinitialize lazy loading for new images
              this.initLazyLoading();
              
              // Animate new products
              this.animateNewProducts();
            }
            
          } catch (error) {
            console.error('Error loading more products:', error);
          } finally {
            isLoading = false;
            loading.classList.add('infinite-scroll-loading--hidden');
          }
        }
      },
      { 
        root: null,
        rootMargin: '100px',
        threshold: 0
      }
    );
    
    observer.observe(trigger);
  }

  /**
   * Initialize lazy loading for images
   */
  initLazyLoading() {
    const images = document.querySelectorAll('img[loading="lazy"]');
    const productGrid = document.querySelector('#product-grid');
    const threshold = productGrid ? parseInt(productGrid.dataset.lazyThreshold) || 100 : 100;
    
    if ('IntersectionObserver' in window) {
      const imageObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              const img = entry.target;
              
              // Load image
              if (img.dataset.src) {
                img.src = img.dataset.src;
                img.removeAttribute('data-src');
              }
              
              // Load srcset if exists
              if (img.dataset.srcset) {
                img.srcset = img.dataset.srcset;
                img.removeAttribute('data-srcset');
              }
              
              // Remove loading attribute
              img.removeAttribute('loading');
              
              // Stop observing this image
              imageObserver.unobserve(img);
            }
          });
        },
        {
          root: null,
          rootMargin: `${threshold}px`,
          threshold: 0
        }
      );
      
      images.forEach(img => imageObserver.observe(img));
    }
  }

  /**
   * Animate new products
   */
  animateNewProducts() {
    const newProducts = document.querySelectorAll('.collection-grid__item:not(.animated)');
    
    newProducts.forEach((product, index) => {
      product.classList.add('animated');
      product.style.animationDelay = `${index * 0.1}s`;
    });
  }

  /**
   * Initialize accessibility features
   */
  initAccessibility() {
    // Trap focus in mobile filter drawer
    this.initFocusTrap();
    
    // Announce filter updates to screen readers
    this.initAriaLive();
    
    // Handle keyboard navigation
    this.initKeyboardNavigation();
  }

  /**
   * Initialize focus trapping in mobile drawer
   */
  initFocusTrap() {
    const drawer = document.querySelector('.mobile-facets__wrapper');
    if (!drawer) return;
    
    drawer.addEventListener('keydown', (e) => {
      if (!drawer.hasAttribute('open')) return;
      
      if (e.key === 'Tab') {
        const focusableElements = drawer.querySelectorAll(
          'button, input, select, textarea, [tabindex="0"], [tabindex="-1"]'
        );
        
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];
        
        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            lastElement.focus();
            e.preventDefault();
          }
        } else {
          if (document.activeElement === lastElement) {
            firstElement.focus();
            e.preventDefault();
          }
        }
      }
    });
  }

  /**
   * Initialize ARIA live regions for announcements
   */
  initAriaLive() {
    // Create live region for filter announcements
    const liveRegion = document.createElement('div');
    liveRegion.setAttribute('aria-live', 'polite');
    liveRegion.setAttribute('aria-atomic', 'true');
    liveRegion.className = 'visually-hidden';
    liveRegion.id = 'filter-announcements';
    document.body.appendChild(liveRegion);
  }

  /**
   * Initialize keyboard navigation
   */
  initKeyboardNavigation() {
    // Handle Enter key on filter button
    const filterButton = document.querySelector('.mobile-facets__open-wrapper');
    filterButton?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        filterButton.click();
      }
    });
    
    // Handle Enter/Space on sort dropdown
    const sortSelect = document.querySelector('#SortBy');
    sortSelect?.addEventListener('keydown', (e) => {
      if (e.key === ' ') {
        e.preventDefault();
        sortSelect.click();
      }
    });
  }

  /**
   * Announce message to screen readers
   */
  announceToScreenReader(message) {
    const liveRegion = document.querySelector('#filter-announcements');
    if (liveRegion) {
      liveRegion.textContent = message;
      setTimeout(() => {
        liveRegion.textContent = '';
      }, 1000);
    }
  }

  /**
   * Initialize collection description toggle functionality
   */
  initCollectionDescription() {
    const toggleButtons = document.querySelectorAll('[id^="CollectionDescriptionToggle-"]');
    
    toggleButtons.forEach(toggle => {
      const sectionId = toggle.id.replace('CollectionDescriptionToggle-', '');
      const content = document.getElementById(`CollectionDescription-${sectionId}`);
      const showText = toggle.querySelector('.collection-description__toggle-text--show');
      const hideText = toggle.querySelector('.collection-description__toggle-text--hide');
      const icon = toggle.querySelector('.collection-description__toggle-icon');
      
      if (!content || !showText || !hideText || !icon) return;

      // Guard against binding the same toggle twice.
      if (toggle.dataset.collectionDescriptionBound === 'true') return;
      toggle.dataset.collectionDescriptionBound = 'true';

      // Measure the collapsed description and show or hide the toggle to match.
      // Two-way (classList.toggle, never a one-way add) so the button can come
      // back when the text reflows taller after load — late fonts, a narrower
      // viewport. The fade gradient is keyed off this same class in CSS, so it
      // stays in lockstep without a second state class.
      const measureToggleVisibility = () => {
        // Nothing to measure if the section was removed from the DOM.
        if (!content.isConnected) return;
        // While expanded max-height is `none`; the toggle must stay visible so
        // the description can be collapsed again.
        if (content.classList.contains('expanded')) return;

        // `none` (or any non-numeric computed value) parses to NaN; fall back to
        // the stylesheet default. An explicit finite check so a computed 0 is
        // honoured rather than silently swapped for 150.
        const parsedMaxHeight = parseInt(getComputedStyle(content).maxHeight, 10);
        const maxHeight = Number.isFinite(parsedMaxHeight) ? parsedMaxHeight : 150;
        const shouldHide = content.scrollHeight <= maxHeight;

        // Hiding the button while it owns focus drops activeElement to <body>,
        // which resets a keyboard or screen-reader user to the top of the
        // document. Hand focus to the description it was describing instead.
        // tabindex is set only at that moment so the container never becomes a
        // permanent extra tab stop.
        if (shouldHide && toggle.contains(document.activeElement)) {
          content.setAttribute('tabindex', '-1');
          content.focus({ preventScroll: true });
        }

        toggle.classList.toggle('collection-description__toggle--hidden', shouldHide);
      };

      measureToggleVisibility();

      // Re-measure whenever the description's own box changes. A ResizeObserver
      // on the measured element covers every reflow trigger, including the two a
      // window resize handler cannot see: a late web-font swap, and rich-text
      // images that finish loading after `document.fonts.ready` has settled.
      //
      // While collapsed the box height is clamped to max-height, so the observer
      // fires exactly when the content crosses the clip threshold in either
      // direction — which is precisely when the toggle state has to change.
      // Width changes (viewport resize) fire it too. `document.fonts.ready` is
      // kept as a cheap backstop for the one case the clamped box hides: content
      // that is already sitting exactly at max-height and grows by a sub-pixel.
      //
      // The callback is deferred to the next frame so it can never re-enter the
      // observer synchronously ("ResizeObserver loop" errors).
      let measureFrame = null;
      const scheduleMeasure = () => {
        if (measureFrame !== null) return;
        measureFrame = requestAnimationFrame(() => {
          measureFrame = null;
          measureToggleVisibility();
        });
      };
      const resizeObserver = new ResizeObserver(scheduleMeasure);
      resizeObserver.observe(content);

      // The theme editor re-renders the section with a brand-new button, so the
      // bound-flag guard cannot stop observers stacking up. Drop this one (and
      // any pending frame) when the section that owns this toggle is unloaded.
      // Compare section ids rather than relying on the unload event's target
      // still containing the toggle — same pattern as assets/theme-editor.js.
      const handleSectionUnload = (event) => {
        if (event.detail.sectionId !== sectionId) return;
        if (measureFrame !== null) cancelAnimationFrame(measureFrame);
        measureFrame = null;
        resizeObserver.disconnect();
        document.removeEventListener('shopify:section:unload', handleSectionUnload);
      };
      document.addEventListener('shopify:section:unload', handleSectionUnload);

      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(measureToggleVisibility).catch(() => {});
      }

      toggle.addEventListener('click', () => {
        const isExpanded = content.classList.contains('expanded');
        
        if (isExpanded) {
          content.classList.remove('expanded');
          toggle.setAttribute('aria-expanded', 'false');
          // Re-measure on the way back down: the viewport may have widened while
          // expanded (measurement bails out in that state), so the text can now
          // fit and the toggle plus its fade gradient must disappear.
          // `scrollHeight` reports the full content extent regardless of
          // max-height, so there is no need to wait for the collapse transition.
          // The ResizeObserver also catches this, but only on the next frame —
          // this synchronous call keeps the focus hand-off in the same task as
          // the user's click, while the button still holds focus.
          measureToggleVisibility();
          this.announceToScreenReader('Description collapsed');
        } else {
          content.classList.add('expanded');
          toggle.setAttribute('aria-expanded', 'true');
          this.announceToScreenReader('Description expanded');
        }
      });
      
      // Handle keyboard navigation
      toggle.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggle.click();
        }
      });
    });
  }
}

// Initialize once, even if this script is loaded by more than one section
// and even if DOMContentLoaded has already fired (deferred/late-injected script).
function initCollectionPage() {
  if (window.collectionPageInstance) return;
  window.collectionPageInstance = new CollectionPage();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCollectionPage, { once: true });
} else {
  initCollectionPage();
}

// Re-bind the collection description toggle when the Shopify theme editor
// re-renders this section (script tags in injected HTML do not re-execute).
document.addEventListener('shopify:section:load', (event) => {
  if (!event.target.querySelector('[id^="CollectionDescriptionToggle-"]')) return;
  if (window.collectionPageInstance) {
    window.collectionPageInstance.initCollectionDescription();
  } else {
    initCollectionPage();
  }
});

// Export for global access if needed
window.CollectionPage = CollectionPage;